/**
 * FishSchool — reef fish as ambience. Voxel-generated like everything else, drawn as one
 * InstancedMesh per body variant, and undulating on the same spine-deform shader the shark
 * uses: the bodies are built along +Z with the nose at high Z, so `spineT` comes out of the
 * mesher already running nose-to-tail and the tail does the work.
 *
 * Motion is a school orbiting a wandering centre, plus a scatter reflex when the shark gets
 * close. Not boids — Phase 3 owns real flocking; this is set dressing that has to feel alive.
 */
import * as THREE from 'three';
import { Rng } from '../core/Rng.ts';
import { OCEAN } from '../data/tuning.ts';
import { SharkMaterial } from '../render/SharkMaterial.ts';
import { bedHeight, waterDepth, lagoonEdge, COAST } from '../world/Coast.ts';
import { buildFish, paint, type FishPlan } from '../world/fish/proceduralFish.js';
import type { SkyEnv } from '../world/DayNight.ts';

/**
 * One entry per distinct species in the reef: body plan, caudal shape, palette and pattern.
 * Each becomes one geometry and one InstancedMesh, and one whole school wears it — real
 * reef schools are single-species and it reads far better than a confetti mix.
 */
const FISH_SPECIES: Array<{
  plan: FishPlan; tail: number; palette: number; pattern: number; patn: number; size: number;
}> = [
  // `size` multiplies the plan's own length (0.82-1.42) and is then trimmed by FISH_SCALE.
  // Keep these as the RELATIVE proportions between species; resize the whole reef with
  // FISH_SCALE rather than editing six numbers and losing the relationships.
  { plan: 'disc',     tail: 0, palette: 0, pattern: 1, patn: 4.5, size: 1.85 },  // butterflyfish
  { plan: 'fusiform', tail: 1, palette: 5, pattern: 0, patn: 3.0, size: 1.55 },  // anthias
  { plan: 'disc',     tail: 3, palette: 4, pattern: 2, patn: 6.0, size: 1.45 },  // humbug damsel
  { plan: 'elongate', tail: 0, palette: 7, pattern: 3, patn: 5.5, size: 1.50 },  // wrasse
  { plan: 'disc',     tail: 2, palette: 2, pattern: 5, patn: 3.0, size: 1.95 },  // regal tang
  { plan: 'trigger',  tail: 0, palette: 6, pattern: 4, patn: 5.0, size: 2.10 },  // triggerfish
];

/**
 * Body rings and points-per-ring. The generator defaults to 38x18, which lands each fish
 * near 6.4k triangles — at a whole reef's worth that outweighs everything else in the scene
 * combined. The fin blades dominate the count and are fixed resolution upstream, so this
 * only claws back the body, but it is free silhouette-wise at this size on screen.
 */
const FISH_NT = 26;
const FISH_NA = 13;

/** one knob for the whole reef's fish size; bodies land at ~0.7-1.0 u nose to tail */
const FISH_SCALE = 1 / 3;

const FLEE_RADIUS = 16;
const FLEE_RADIUS_SQ = FLEE_RADIUS * FLEE_RADIUS;

/** how far along the coast a school may drift from the player before it is recycled */
const WINDOW_X = 165;

/**
 * Cross-shore homes, weighted. Reef fish are called reef fish for a reason — most of the
 * population sits on the crest — but a coast with nothing at all in the lagoon reads as two
 * separate places rather than one shore.
 *
 * `column` is where in the available water the school hangs: planktivores stack up off the
 * reef face, grazers work the bottom, so the spread matters as much as the average.
 */
const SHOALS: Array<{ z: [number, number]; column: [number, number]; weight: number }> = [
  { z: [56, 104], column: [0.14, 0.72], weight: 2.2 },   // the reef crest
  { z: [40, 62], column: [0.20, 0.60], weight: 1.0 },    // the back-reef margin
  { z: [16, 46], column: [0.25, 0.68], weight: 0.9 },    // the lagoon
  { z: [102, 126], column: [0.30, 0.80], weight: 0.7 },  // out over the fore-reef
];

interface Fish {
  school: number;
  variant: number;
  /** offset within the school, in the school's local frame */
  ox: number;
  oy: number;
  oz: number;
  phase: number;
  wobble: number;
  x: number;
  y: number;
  z: number;
  heading: number;
  /** 0..1 panic, decays; drives both speed and how far it breaks formation */
  flee: number;
  fx: number;
  fz: number;
}

interface School {
  cx: number;
  cz: number;
  cy: number;
  heading: number;
  turnPhase: number;
  speed: number;
  /** cross-shore home band, held loosely */
  band: [number, number];
  /** fraction of the local water column the school hangs at */
  column: number;
}


export class FishSchool {
  readonly group = new THREE.Group();

  private readonly meshes: THREE.InstancedMesh[] = [];
  private readonly materials: SharkMaterial[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly fish: Fish[] = [];
  private readonly schools: School[] = [];
  /** per variant: the fish drawn by that InstancedMesh, in instance order */
  private readonly byVariant: Fish[][] = [];

  private readonly rng: Rng;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly pos = new THREE.Vector3();
  private readonly scl = new THREE.Vector3(1, 1, 1);
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(seed = 0xf15) {
    const rng = new Rng(seed >>> 0);
    this.rng = rng;
    this.group.name = 'reef-fish';

    for (let v = 0; v < FISH_SPECIES.length; v++) {
      const spec = FISH_SPECIES[v];
      const m = buildFish({
        plan: spec.plan,
        seed: rng.int(1, 1 << 28),
        depth: rng.range(0.9, 1.12),
        compress: rng.range(0.85, 1.1),
        fins: rng.range(0.9, 1.15),
        tail: spec.tail,
        NT: FISH_NT,
        NA: FISH_NA,
      });
      paint(m, {
        palette: spec.palette,
        pattern: spec.pattern,
        patn: spec.patn,
        contrast: rng.range(0.72, 0.95),
        hue: rng.range(-14, 14),
      });

      // Same colour-space correction the coral needs: the generator authors livery in
      // display space, this pipeline shades in linear with ACES on the end.
      const col = m.colors;
      for (let i = 0; i < col.length; i++) {
        const c = col[i];
        col[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      }

      // spineT straight off the generator's body coordinate: it already runs 0 at the nose
      // to 1 at the peduncle, which is exactly the convention the swim shader wants. The
      // fins overshoot slightly past 1, so clamp.
      const nVerts = m.positions.length / 3;
      const spineT = new Float32Array(nVerts);
      for (let i = 0; i < nVerts; i++) {
        const t = m.body[i * 4];
        spineT[i] = t < 0 ? 0 : t > 1 ? 1 : t;
      }

      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(m.positions, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(m.normals, 3));
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      g.setAttribute('spineT', new THREE.BufferAttribute(spineT, 1));
      g.setIndex(new THREE.BufferAttribute(m.indices, 1));
      // Built nose-at-+X; this world is nose-at-+Z. Rotating the geometry rather than the
      // instances keeps the swim shader's lateral axis lined up with the body.
      g.rotateY(-Math.PI / 2);
      const sz = spec.size * FISH_SCALE;
      g.scale(sz, sz, sz);
      g.computeBoundingSphere();
      if (g.boundingSphere) g.boundingSphere.radius *= 1.8;
      this.geometries.push(g);
      this.byVariant.push([]);
    }

    let shoalTotal = 0;
    for (const sh of SHOALS) shoalTotal += sh.weight;

    for (let s = 0; s < OCEAN.fishSchools; s++) {
      let r = rng.next() * shoalTotal;
      let shoal = SHOALS[0];
      for (const sh of SHOALS) {
        r -= sh.weight;
        if (r <= 0) { shoal = sh; break; }
      }
      // Spread along the coast, not on a disc: the arena is a strip now, and a school
      // spawned 150 u out to sea is a school past the fog line on the first frame.
      const cx = rng.range(-WINDOW_X, WINDOW_X);
      const cz = rng.range(shoal.z[0], shoal.z[1]);
      const column = rng.range(shoal.column[0], shoal.column[1]);
      this.schools.push({
        cx,
        cz,
        cy: bedHeight(cx, cz) + 1.6,
        heading: rng.range(0, Math.PI * 2),
        turnPhase: rng.range(0, Math.PI * 2),
        speed: OCEAN.fishSpeed * rng.range(0.8, 1.25),
        band: shoal.z,
        column,
      });

      // one variant per school: real reef schools are single-species, and it reads far
      // better than a confetti mix
      const variant = rng.int(0, FISH_SPECIES.length);
      for (let i = 0; i < OCEAN.fishPerSchool; i++) {
        const f: Fish = {
          school: s,
          variant,
          ox: rng.range(-4.5, 4.5),
          oy: rng.range(-2.2, 2.2),
          oz: rng.range(-5.5, 5.5),
          phase: rng.range(0, Math.PI * 2),
          wobble: rng.range(0.6, 1.5),
          x: 0, y: 0, z: 0,
          heading: 0,
          flee: 0,
          fx: 0, fz: 0,
        };
        this.fish.push(f);
        this.byVariant[variant].push(f);
      }
    }

    for (let v = 0; v < this.geometries.length; v++) {
      const list = this.byVariant[v];
      if (list.length === 0) continue;
      const material = new SharkMaterial({ instanced: true });
      // Fin blades are thin two-sided shells with a scalloped trailing edge; back-face
      // culling eats half of every fin at grazing angles.
      material.side = THREE.DoubleSide;
      // Amplitude is in WORLD units, so it has to track FISH_SCALE or resizing the reef
      // silently changes how hard everything swims. Small fish also beat much faster.
      material.setSwim(0.13 * FISH_SCALE, 13);
      this.materials.push(material);
      const inst = new THREE.InstancedMesh(this.geometries[v], material, list.length);
      const phase = new Float32Array(list.length);
      for (let i = 0; i < list.length; i++) phase[i] = list[i].phase;
      this.geometries[v].setAttribute('aPhase', new THREE.InstancedBufferAttribute(phase, 1));
      inst.frustumCulled = false;
      inst.name = `fish-${v}`;
      this.meshes.push(inst);
      this.group.add(inst);
    }
  }

  get count(): number {
    return this.fish.length;
  }

  /** @param sharkX/@param sharkZ the player, so the reef reacts to it */
  update(dt: number, time: number, sharkX: number, sharkZ: number): void {
    for (const sc of this.schools) {
      // lazy wander: a slow sine on the heading, which keeps schools curving rather than
      // ping-ponging between random targets
      sc.turnPhase += dt * 0.23;
      sc.heading += Math.sin(sc.turnPhase) * dt * 0.55;

      /* Station-keeping, cross-shore. A school belongs to a band and is pulled back into it
         when it strays, and it feels the bottom coming up by DEPTH rather than by Z — the
         shoreline meanders by ±26 u, so a fixed limit would march a whole school onto a
         headland. Applied as a bend in the heading, not a teleport, so the school arcs round
         the way a real one does. */
      let wantX = Math.sin(sc.heading);
      let wantZ = Math.cos(sc.heading);
      if (sc.cz < sc.band[0]) wantZ += Math.min(1.4, (sc.band[0] - sc.cz) * 0.09);
      else if (sc.cz > sc.band[1]) wantZ -= Math.min(1.4, (sc.cz - sc.band[1]) * 0.09);
      const shallow = COAST.minSwimDepth + 1.5 - waterDepth(sc.cx, sc.cz);
      if (shallow > 0) wantZ += shallow * 0.7;
      if (sc.cz > COAST.edgeZ - 8) wantZ -= (sc.cz - (COAST.edgeZ - 8)) * 0.3;
      // and the bay has ends
      const edge = lagoonEdge(sc.cx);
      if (edge > 0.02) wantX -= Math.sign(sc.cx) * edge * 2.4;

      let delta = Math.atan2(wantX, wantZ) - sc.heading;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      sc.heading += Math.max(-1.1 * dt, Math.min(1.1 * dt, delta));

      sc.cx += Math.sin(sc.heading) * sc.speed * dt;
      sc.cz += Math.cos(sc.heading) * sc.speed * dt;

      /* The coast is endless, so the population is a moving window, not a fixed cast: a
         school that falls behind is recycled to the far side at a fresh cross-shore station.
         Nothing is ever "left behind" no matter how far you swim. */
      const off = sc.cx - sharkX;
      if (Math.abs(off) > WINDOW_X) {
        sc.cx = Math.max(-COAST.lagoonHalf + 30,
          Math.min(COAST.lagoonHalf - 30, sharkX - Math.sign(off) * (WINDOW_X - 26)));
        sc.cz = this.rng.range(sc.band[0], sc.band[1]);
        sc.heading = off > 0 ? -Math.PI / 2 : Math.PI / 2;
        for (const f of this.fish) {
          if (this.schools[f.school] !== sc) continue;
          f.x = sc.cx + f.ox;
          f.z = sc.cz + f.oz;
        }
      }

      // Hang in the local column rather than at an absolute height: over a 14 u lagoon and a
      // 4 u reef flat one fixed Y is either scraping the coral or breaking the surface.
      const floor = bedHeight(sc.cx, sc.cz) + 1.6;
      const room = Math.max(0, OCEAN.surfaceHeight - 1.8 - floor);
      sc.cy += (floor + room * sc.column - sc.cy) * (1 - Math.exp(-1.6 * dt));
    }

    for (const f of this.fish) {
      const sc = this.schools[f.school];
      const s = Math.sin(sc.heading);
      const c = Math.cos(sc.heading);

      // formation offset rotated into the school's frame, plus a personal wander
      const bx = f.ox * c + f.oz * s;
      const bz = -f.ox * s + f.oz * c;
      let tx = sc.cx + bx + Math.sin(time * f.wobble + f.phase) * 0.9;
      let tz = sc.cz + bz + Math.cos(time * f.wobble * 0.8 + f.phase) * 0.9;

      const dx = f.x - sharkX;
      const dz = f.z - sharkZ;
      const distSq = dx * dx + dz * dz;
      if (distSq < FLEE_RADIUS_SQ) {
        f.flee = 1;
        const inv = 1 / Math.max(0.001, Math.sqrt(distSq));
        f.fx = dx * inv;
        f.fz = dz * inv;
      } else {
        f.flee = Math.max(0, f.flee - dt * 0.85);
      }
      if (f.flee > 0) {
        const burst = f.flee * 11;
        tx += f.fx * burst;
        tz += f.fz * burst;
      }

      const k = 1 - Math.exp(-(2.4 + f.flee * 5) * dt);
      f.x += (tx - f.x) * k;
      f.z += (tz - f.z) * k;

      const floor = bedHeight(f.x, f.z);
      const wantY = Math.max(
        floor + 1.6,
        Math.min(OCEAN.surfaceHeight - 1.5, sc.cy + f.oy + Math.sin(time * 0.6 + f.phase) * 0.7),
      );
      f.y += (wantY - f.y) * (1 - Math.exp(-2.0 * dt));

      // face the way you are actually going; below a threshold keep the last heading so a
      // hovering fish does not spin on the spot from numerical noise
      const vx = tx - f.x;
      const vz = tz - f.z;
      if (vx * vx + vz * vz > 1e-4) f.heading = Math.atan2(vx, vz);
    }

    for (let v = 0; v < this.meshes.length; v++) {
      const inst = this.meshes[v];
      const list = this.byVariant[v];
      for (let i = 0; i < list.length; i++) {
        const f = list[i];
        this.pos.set(f.x, f.y, f.z);
        this.q.setFromAxisAngle(this.up, f.heading);
        this.m.compose(this.pos, this.q, this.scl);
        inst.setMatrixAt(i, this.m);
      }
      inst.instanceMatrix.needsUpdate = true;
    }

    for (const mat of this.materials) mat.setTime(time);
  }

  setEnv(env: SkyEnv): void {
    for (const m of this.materials) m.setEnv(env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    for (const mat of this.materials) mat.setShadow(map, matrix, strength);
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const m of this.meshes) m.dispose();
    for (const g of this.geometries) g.dispose();
    for (const mat of this.materials) mat.dispose();
    this.meshes.length = 0;
    this.geometries.length = 0;
    this.materials.length = 0;
  }
}
